import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Button, IconButton, TextField,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, Checkbox, Autocomplete, Chip, CircularProgress,
  Tooltip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useTranslation } from 'react-i18next';
import { getPhoneCatalog, getPhoneCatalogCountries, getPhoneCatalogProviders, getPhoneCatalogCities } from '../../../../services/phoneCatalogApi';
import { usePricing } from '../../../../contexts/PricingContext';
import { useUserPreferences } from '../../../../contexts/UserPreferencesContext';
import { convertToUSD } from '../../../../utils/currencyUtils';
import DocsNotePopovers from './DocsNotePopovers';

export { convertToUSD };

const REGION_PRIORITY = {
  ua: 'Ukraine',
  eu: 'Poland',
  asia: 'Kazakhstan',
};

const sortCountries = (countries, region) => {
  const priority = REGION_PRIORITY[region];
  return [...countries].sort((a, b) => {
    if (priority && a.name === priority) return -1;
    if (priority && b.name === priority) return 1;
    if (a.is_top_country && !b.is_top_country) return -1;
    if (!a.is_top_country && b.is_top_country) return 1;
    return a.name.localeCompare(b.name);
  });
};

const formatCatalogName = (entry) => {
  const parts = [entry.country];
  if (entry.city) parts.push(entry.city);
  if (entry.provider) return `${parts.join(', ')} — ${entry.provider}`;
  return parts.join(', ');
};

const PhoneCatalogBrowser = ({ open, onClose, onAddNumbers, enablePhoneActivation }) => {
  const { t } = useTranslation();
  const { currencyRates } = usePricing();
  let region = 'ua';
  try {
    const prefs = useUserPreferences();
    region = prefs.region;
  } catch { /* fallback */ }

  const [countries, setCountries] = useState([]);
  const [providers, setProviders] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState({}); // { [id]: { ...entry, quantity: N } }
  const [docsPopover, setDocsPopover] = useState({ anchorEl: null, text: '' });
  const [notePopover, setNotePopover] = useState({ anchorEl: null, text: '' });

  const limit = 50;

  // Load filter options on open
  useEffect(() => {
    if (!open) return;
    const load = async () => {
      try {
        const [countriesRes, providersRes, citiesRes] = await Promise.all([
          getPhoneCatalogCountries(),
          getPhoneCatalogProviders(),
          getPhoneCatalogCities(),
        ]);
        setCountries(sortCountries(countriesRes.data?.data || [], region));
        setProviders((providersRes.data?.data || []).sort());
        setCities((citiesRes.data?.data || []).sort());
      } catch (err) {
        console.error('Failed to load catalog filters', err);
      }
    };
    load();
  }, [open, region]);

  // Fetch catalog entries
  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: page + 1, limit };
      if (selectedCountry) params.country = selectedCountry;
      if (selectedCity) params.city = selectedCity;
      if (selectedProvider) params.provider = selectedProvider;
      if (searchQuery) params.search = searchQuery;
      const res = await getPhoneCatalog(params);
      const payload = res.data;
      setEntries(payload?.data || []);
      setTotal(payload?.total || 0);
    } catch (err) {
      console.error('Failed to fetch catalog', err);
    } finally {
      setLoading(false);
    }
  }, [page, selectedCountry, selectedCity, selectedProvider, searchQuery]);

  useEffect(() => {
    if (open) fetchEntries();
  }, [open, fetchEntries]);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setSelected({});
      setPage(0);
      setSelectedCountry(null);
      setSelectedCity(null);
      setSelectedProvider(null);
      setSearchQuery('');
    }
  }, [open]);

  // Reset page when filters change
  useEffect(() => { setPage(0); }, [selectedCountry, selectedCity, selectedProvider, searchQuery]);

  const selectedCount = Object.keys(selected).length;

  const isSelected = (id) => !!selected[id];

  const handleToggle = (entry) => {
    const id = entry._id || entry.id;
    setSelected(prev => {
      if (prev[id]) {
        const next = { ...prev };
        delete next[id];
        return next;
      }
      return { ...prev, [id]: { ...entry, quantity: 1 } };
    });
  };

  const handleQuantityChange = (id, value) => {
    setSelected(prev => {
      if (!prev[id]) return prev;
      return { ...prev, [id]: { ...prev[id], quantity: Math.max(1, Number(value) || 1) } };
    });
  };

  const handleDocsClick = (e, text) => {
    e.stopPropagation();
    setDocsPopover({ anchorEl: e.currentTarget, text });
  };

  const handleNoteClick = (e, text) => {
    e.stopPropagation();
    setNotePopover({ anchorEl: e.currentTarget, text });
  };

  const handleAdd = () => {
    const items = Object.values(selected).map(entry => ({
      catalogId: entry._id || entry.id,
      name: formatCatalogName(entry),
      price: convertToUSD(entry.monthly_fee, entry.currency, currencyRates) || 0,
      activationFee: convertToUSD(entry.activation_fee, entry.currency, currencyRates) || 0,
      quantity: entry.quantity || 1,
      catalogCurrency: entry.currency,
      isFromPrice: entry.is_from_price || false,
      comment: entry.comment || '',
      requiredDocuments: entry.required_documents || '',
      isFreeForTrial: entry.is_free_for_trial || false,
    }));
    onAddNumbers(items);
  };

  const fmtPrice = (fee, currency) => {
    if (fee == null) return '—';
    const sym = currency === 'EUR' ? '€' : currency === 'UAH' ? '₴' : '$';
    return `${sym}${fee}`;
  };

  // Filter cities based on selected country (from current results)
  const filteredCities = useMemo(() => {
    if (!selectedCountry) return cities;
    const countryCities = new Set(entries.filter(e => e.country === selectedCountry).map(e => e.city).filter(Boolean));
    // If we have entries filtered by country, show their cities; otherwise show all
    return countryCities.size > 0 ? [...countryCities].sort() : cities;
  }, [selectedCountry, entries, cities]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 1 } }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {t('calculator.phoneCatalog', 'Phone Number Catalog')}
        </Typography>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: 1, pt: 2, overflow: 'visible' }}>
        {/* Filters */}
        <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap', pt: 0.5 }}>
          <Autocomplete
            size="small"
            options={countries.map(c => c.name)}
            value={selectedCountry}
            onChange={(_, v) => { setSelectedCountry(v); setSelectedCity(null); }}
            renderInput={(params) => <TextField {...params} label={t('calculator.selectCountry', 'Country')} />}
            sx={{ minWidth: 160, flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
          />
          <Autocomplete
            size="small"
            options={filteredCities}
            value={selectedCity}
            onChange={(_, v) => setSelectedCity(v)}
            renderInput={(params) => <TextField {...params} label={t('calculator.selectCity', 'City')} />}
            sx={{ minWidth: 140, flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
          />
          <Autocomplete
            size="small"
            options={providers}
            value={selectedProvider}
            onChange={(_, v) => setSelectedProvider(v)}
            renderInput={(params) => <TextField {...params} label={t('calculator.selectProvider', 'Provider')} />}
            sx={{ minWidth: 140, flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
          />
          <TextField
            size="small"
            placeholder={t('calculator.searchNumbers', 'Search...')}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            sx={{ minWidth: 120, flex: 0.7, '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
          />
        </Box>

        {/* Table */}
        <TableContainer sx={{ border: '1px solid #eee', borderRadius: 1, maxHeight: 400 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={{ '& .MuiTableCell-root': { fontWeight: 600, fontSize: 12, color: 'rgba(0,0,0,0.6)', bgcolor: '#fafafa' } }}>
                <TableCell padding="checkbox" sx={{ width: 42 }} />
                <TableCell>{t('calculator.country', 'Country')}</TableCell>
                <TableCell>{t('calculator.city', 'City')}</TableCell>
                <TableCell>{t('calculator.provider', 'Provider')}</TableCell>
                <TableCell>{t('calculator.code', 'Code')}</TableCell>
                <TableCell align="right">{t('calculator.monthlyFee', 'Monthly')}</TableCell>
                {enablePhoneActivation && (
                  <TableCell align="right">{t('calculator.activationFee', 'Activation')}</TableCell>
                )}
                <TableCell align="center" sx={{ width: 50 }}>{t('calculator.currency', 'Cur.')}</TableCell>
                <TableCell align="center" sx={{ width: 36 }}>{t('calculator.docsShort', 'Docs')}</TableCell>
                <TableCell align="center" sx={{ width: 36 }}>{t('calculator.commentShort', 'Note')}</TableCell>
                <TableCell align="center" sx={{ width: 60 }}>{t('calculator.freeForTrial', 'Free trial')}</TableCell>
                <TableCell align="center" sx={{ width: 60 }}>{t('calculator.quantity', 'Qty')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={enablePhoneActivation ? 12 : 11} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : entries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={enablePhoneActivation ? 12 : 11} align="center" sx={{ py: 4, color: 'rgba(0,0,0,0.45)' }}>
                    {t('calculator.noResults', 'No numbers found')}
                  </TableCell>
                </TableRow>
              ) : (
                entries.map(entry => {
                  const id = entry._id || entry.id;
                  const checked = isSelected(id);
                  return (
                    <TableRow
                      key={id}
                      hover
                      onClick={() => handleToggle(entry)}
                      sx={{ cursor: 'pointer', '& .MuiTableCell-root': { py: 1, borderColor: '#eee' } }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox size="small" checked={checked} sx={{ '& .MuiSvgIcon-root': { fontSize: 18 } }} />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {entry.country}
                          {entry.is_top_country && (
                            <Chip label="TOP" size="small" sx={{ height: 16, fontSize: 10, fontWeight: 700, bgcolor: '#FFF3E0', color: '#E65100', borderRadius: 1 }} />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{entry.city}</TableCell>
                      <TableCell>{entry.provider}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{entry.code}</TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                          {entry.is_from_price && (
                            <Chip label={t('calculator.fromPrice', 'FROM')} size="small" sx={{ height: 16, fontSize: 9, fontWeight: 700, bgcolor: '#FFF8E1', color: '#F57F17', borderRadius: 1 }} />
                          )}
                          {fmtPrice(entry.monthly_fee, entry.currency)}
                        </Box>
                      </TableCell>
                      {enablePhoneActivation && (
                        <TableCell align="right">{fmtPrice(entry.activation_fee, entry.currency)}</TableCell>
                      )}
                      <TableCell align="center" sx={{ fontSize: 11, color: 'rgba(0,0,0,0.5)' }}>{entry.currency}</TableCell>
                      <TableCell align="center" sx={{ px: 0 }}>
                        {entry.required_documents ? (
                          <IconButton
                            size="small"
                            onClick={(e) => handleDocsClick(e, entry.required_documents)}
                            sx={{ p: 0.3 }}
                          >
                            <DescriptionOutlinedIcon sx={{ fontSize: 16, color: '#1976d2' }} />
                          </IconButton>
                        ) : null}
                      </TableCell>
                      <TableCell align="center" sx={{ px: 0 }}>
                        {entry.comment ? (
                          <IconButton
                            size="small"
                            onClick={(e) => handleNoteClick(e, entry.comment)}
                            sx={{ p: 0.3 }}
                          >
                            <ChatBubbleOutlineIcon sx={{ fontSize: 16, color: '#1976d2' }} />
                          </IconButton>
                        ) : null}
                      </TableCell>
                      <TableCell align="center" sx={{ px: 0 }} onClick={e => e.stopPropagation()}>
                        {entry.is_free_for_trial ? (
                          <Tooltip title={t('calculator.freeForTrialYes', 'Free for trial')} arrow>
                            <CheckCircleOutlineIcon sx={{ fontSize: 16, color: '#00C853' }} />
                          </Tooltip>
                        ) : null}
                      </TableCell>
                      <TableCell align="center" onClick={e => e.stopPropagation()}>
                        {checked ? (
                          <TextField
                            value={selected[id]?.quantity || 1}
                            onChange={e => handleQuantityChange(id, e.target.value)}
                            type="number"
                            size="small"
                            variant="standard"
                            inputProps={{ min: 1, style: { textAlign: 'center', width: 36, fontWeight: 600 } }}
                            InputProps={{ disableUnderline: true }}
                          />
                        ) : (
                          <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.3)' }}>—</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={limit}
          rowsPerPageOptions={[50]}
          sx={{ borderTop: '1px solid #eee' }}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.6)' }}>
          {selectedCount > 0
            ? t('calculator.itemsSelected', '{{count}} selected', { count: selectedCount })
            : ''}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose} sx={{ borderRadius: 1, textTransform: 'none' }}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            disabled={selectedCount === 0}
            onClick={handleAdd}
            sx={{
              borderRadius: 1,
              textTransform: 'none',
              bgcolor: '#00C853',
              '&:hover': { bgcolor: '#00A844' },
            }}
          >
            {t('calculator.addToOffer', 'Add to offer')} {selectedCount > 0 && `(${selectedCount})`}
          </Button>
        </Box>
      </DialogActions>

      <DocsNotePopovers
        docs={{
          anchorEl: docsPopover.anchorEl,
          text: docsPopover.text,
          onClose: () => setDocsPopover({ anchorEl: null, text: '' }),
        }}
        note={{
          anchorEl: notePopover.anchorEl,
          text: notePopover.text,
          onClose: () => setNotePopover({ anchorEl: null, text: '' }),
        }}
      />
    </Dialog>
  );
};

export default React.memo(PhoneCatalogBrowser);
